/**
 * Which shape of training program the user has asked for.
 *
 * "Split" here means how the week's work is divided between workouts. Today the
 * app always decided this by itself; #4591 lets the user choose.
 *
 * `Optimal` is what the app has always done and is what every existing user is
 * on. It must keep producing exactly the same programs — see
 * `design-docs/training-splits.md`.
 */
export enum TrainingSplitMode {
  /** The app decides the shape itself. This is the long-standing behaviour. */
  Optimal = 'optimal',
  /** Three kinds of workout that rotate: pushing muscles, pulling muscles, legs. */
  PushPullLegs = 'pushPullLegs',
  /** Two kinds of workout that alternate: upper body, lower body. */
  UpperLower = 'upperLower',
  /** Every workout trains every major muscle group. */
  FullBody = 'fullBody',
}

/** Every value the enum can take, in the order the user sees them. */
export const ALL_TRAINING_SPLIT_MODES: TrainingSplitMode[] = [
  TrainingSplitMode.Optimal,
  TrainingSplitMode.PushPullLegs,
  TrainingSplitMode.UpperLower,
  TrainingSplitMode.FullBody,
];

/** Is this value one of the modes we know about? */
export function isTrainingSplitMode(value: unknown): value is TrainingSplitMode {
  return typeof value === 'string' && (ALL_TRAINING_SPLIT_MODES as string[]).includes(value);
}

/**
 * The ONLY place that turns a stored preference into a mode to solve with.
 *
 * A missing value means `Optimal`, so every user who signed up before this
 * feature existed keeps exactly the program they have with no migration.
 *
 * A value we do not recognise also resolves to `Optimal`, because refusing to
 * build a program is a far worse outcome for that user than building the
 * default one — but it is reported, because our own UI can only ever write a
 * value from a fixed list, so anything else means something upstream is wrong.
 */
export function resolveTrainingSplitMode(declaredState: { trainingSplitMode?: TrainingSplitMode } | null | undefined): TrainingSplitMode {
  const stored = declaredState?.trainingSplitMode;
  if (stored === undefined || stored === null) {
    return TrainingSplitMode.Optimal;
  }
  if (!isTrainingSplitMode(stored)) {
    console.error(`[PROBLEM] unknown-training-split-mode: stored value ${JSON.stringify(stored)} is not a known training split mode; using Optimal`);
    return TrainingSplitMode.Optimal;
  }
  return stored;
}
